import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ShieldCheck, AlertTriangle, MapPin, Calendar, Trophy, Mail, Loader2, GraduationCap, Star } from "lucide-react";
import { Button } from "../components/ui/button";

const API = process.env.REACT_APP_BACKEND_URL;

export default function SharedProfilePage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${API}/api/share/${encodeURIComponent(token)}`, { credentials: "include" });
        const txt = await r.text();
        let body = {};
        try { body = JSON.parse(txt); } catch {}
        if (cancelled) return;
        if (!r.ok) {
          setError(body.detail || "Unable to load shared profile");
        } else {
          setData(body);
        }
      } catch (e) {
        if (!cancelled) setError(e.message || "Network error");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  if (loading) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center">
        <div className="flex items-center gap-2 text-white/60">
          <Loader2 className="animate-spin" size={18} /> Loading private scouting report…
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center p-6" data-testid="share-error">
        <div className="max-w-md text-center bg-red-950/30 border border-red-500/30 rounded-2xl p-8">
          <AlertTriangle size={28} className="text-red-400 mx-auto mb-3" />
          <h1 className="text-xl font-black tracking-tight">Link Unavailable</h1>
          <p className="text-white/60 mt-2 text-sm">{error}</p>
          <p className="text-white/40 mt-4 text-xs">If the athlete is still interested in sharing their profile, ask them to issue a new link from their NSB Player Dashboard.</p>
          <Link to="/"><Button className="mt-6 bg-white text-black hover:bg-gray-100" data-testid="share-back-home">Return to NSB</Button></Link>
        </div>
      </div>
    );
  }

  const { share, player } = data;
  const p = player || {};
  const toolGrades = p.tool_grades || p.nsb_grades || {};
  const metrics = p.metrics || {};
  const selfMetrics = p.self_reported_metrics || {};
  const allMetrics = { ...selfMetrics, ...metrics };
  const scoutingNotes = p.scouting_notes || {};

  return (
    <div className="min-h-screen bg-black text-white" data-testid="shared-profile-page">
      {/* Confidential header banner */}
      <div className="bg-gradient-to-r from-amber-600/20 via-amber-500/10 to-amber-600/20 border-b border-amber-500/30 px-4 py-3" data-testid="share-confidential-bar">
        <div className="max-w-5xl mx-auto flex items-center gap-3 text-amber-200">
          <ShieldCheck size={16} />
          <div className="flex-1 text-xs sm:text-sm">
            <span className="font-bold">Confidential — Private Scouting Link</span>
            <span className="text-amber-300/80 ml-2">Shared with {share.coach_name || share.coach_email}{share.coach_school ? ` (${share.coach_school})` : ""}.</span>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        {/* Header */}
        <div className="flex flex-col sm:flex-row gap-6 sm:gap-8 items-start sm:items-center mb-10">
          <div className="w-32 h-32 sm:w-40 sm:h-40 rounded-2xl overflow-hidden bg-white/5 border border-white/10 flex-shrink-0">
            {p.profile_image ? (
              <img src={p.profile_image} alt={p.name} className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-white/20"><Star size={36} /></div>
            )}
          </div>
          <div className="flex-1">
            <div className="inline-flex items-center gap-1.5 text-[10px] tracking-[0.25em] uppercase text-amber-400 font-bold mb-2">
              <ShieldCheck size={12} /> NSB Verified Athlete
            </div>
            <h1 className="text-4xl sm:text-5xl font-black tracking-tight" data-testid="share-player-name">{p.name || "Athlete"}</h1>
            <div className="flex flex-wrap gap-3 mt-3 text-sm text-white/60">
              {p.position_primary && <span className="flex items-center gap-1"><Trophy size={12} /> {p.position_primary}</span>}
              {p.graduation_year && <span className="flex items-center gap-1"><Calendar size={12} /> Class of {p.graduation_year}</span>}
              {p.school && <span className="flex items-center gap-1"><GraduationCap size={12} /> {p.school}</span>}
              {p.location && <span className="flex items-center gap-1"><MapPin size={12} /> {p.location}</span>}
            </div>
            {share.personal_note && (
              <blockquote className="mt-4 border-l-2 border-amber-400 pl-4 italic text-sm text-white/70 max-w-2xl">
                "{share.personal_note}"
              </blockquote>
            )}
          </div>
        </div>

        {/* Body & demographic */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-10">
          {p.height && <StatPill label="Height" value={p.height} />}
          {p.weight && <StatPill label="Weight" value={`${p.weight} lbs`} />}
          {p.bats && <StatPill label="Bats" value={p.bats} />}
          {p.throws && <StatPill label="Throws" value={p.throws} />}
        </div>

        {/* Verified Performance Metrics */}
        {Object.keys(allMetrics).length > 0 && (
          <Section title="Verified Performance Metrics" data-testid="share-section-metrics">
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
              {Object.entries(allMetrics).map(([k, v]) => (
                <MetricCard key={k} label={prettyLabel(k)} value={String(v)} />
              ))}
            </div>
          </Section>
        )}

        {/* Tool Grades */}
        {Object.keys(toolGrades).length > 0 && (
          <Section title="NSB Tool Grades (20–80 Scale)" data-testid="share-section-grades">
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
              {Object.entries(toolGrades).map(([tool, grade]) => {
                const val = typeof grade === "object" ? (grade.current || grade.value) : grade;
                if (!val) return null;
                return <GradeCard key={tool} label={prettyLabel(tool)} value={val} />;
              })}
            </div>
          </Section>
        )}

        {/* Private Scouting Notes */}
        {Object.keys(scoutingNotes).length > 0 && (
          <Section title="Private Scouting Notes" data-testid="share-section-notes">
            <div className="space-y-3">
              {Object.entries(scoutingNotes).map(([k, v]) => (
                <div key={k} className="rounded-xl border border-white/10 bg-white/[0.03] p-4">
                  <div className="text-[10px] tracking-wider uppercase text-amber-400 font-bold mb-1">{prettyLabel(k)}</div>
                  <p className="text-sm text-white/80 leading-relaxed whitespace-pre-wrap">{String(v)}</p>
                </div>
              ))}
            </div>
          </Section>
        )}

        {/* Bio */}
        {p.bio && (
          <Section title="Bio" data-testid="share-section-bio">
            <p className="text-sm text-white/75 leading-relaxed whitespace-pre-wrap">{p.bio}</p>
          </Section>
        )}

        {/* Highlight Video */}
        {(p.highlight_video_url || p.video_url) && (
          <Section title="Highlight Video" data-testid="share-section-video">
            <div className="aspect-video rounded-xl overflow-hidden bg-black border border-white/10">
              <iframe
                src={normalizeVideoEmbed(p.highlight_video_url || p.video_url)}
                title="Highlight"
                className="w-full h-full"
                allow="accelerated-2d-canvas; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
          </Section>
        )}

        {/* Scouting Report PDF */}
        {p.has_scouting_report && (
          <Section title="Full Scouting Report" data-testid="share-section-report">
            <a href={`${API}/api/players/${p.athlete_id}/scouting-report.pdf?share_token=${encodeURIComponent(token)}`} target="_blank" rel="noopener noreferrer">
              <Button className="bg-amber-500 hover:bg-amber-400 text-black font-bold" data-testid="share-download-report-btn">
                Download Full Scouting Report (PDF)
              </Button>
            </a>
          </Section>
        )}

        {/* Footer */}
        <div className="mt-12 pt-8 border-t border-white/10 text-center">
          <p className="text-xs text-white/40 leading-relaxed max-w-2xl mx-auto">
            This private link was issued by {p.name || "the athlete"} via the NSB Player Dashboard. Do not forward.
            Single-recipient links are tied to <span className="text-white/60 font-mono">{share.coach_email}</span>.
          </p>
          <p className="text-xs text-white/30 mt-3">
            Questions? <a href="mailto:rod@scoutseyeapp.com" className="text-amber-400 hover:text-amber-300"><Mail size={11} className="inline mr-0.5" />rod@scoutseyeapp.com</a>
          </p>
          <p className="text-[10px] text-white/20 mt-6">
            © 2026 National Scouting Bureau, LLC. Patent Pending. NSB Verified&trade;.
          </p>
        </div>
      </div>
    </div>
  );
}

function Section({ title, children, ...props }) {
  return (
    <div className="mb-8" {...props}>
      <h2 className="text-lg font-black tracking-tight text-white mb-3">{title}</h2>
      {children}
    </div>
  );
}
function StatPill({ label, value }) {
  return (
    <div className="rounded-lg border border-white/10 bg-white/[0.03] px-4 py-3">
      <div className="text-[10px] uppercase tracking-wider text-white/40 font-bold">{label}</div>
      <div className="text-base font-bold text-white mt-0.5">{value}</div>
    </div>
  );
}
function MetricCard({ label, value }) {
  return (
    <div className="rounded-lg border border-amber-500/20 bg-amber-500/[0.05] px-4 py-3">
      <div className="text-[10px] uppercase tracking-wider text-amber-300/80 font-bold">{label}</div>
      <div className="text-xl font-black text-white mt-0.5">{value}</div>
    </div>
  );
}
function GradeCard({ label, value }) {
  const n = parseFloat(value);
  const color = n >= 70 ? "text-green-400" : n >= 60 ? "text-blue-400" : n >= 50 ? "text-amber-400" : "text-white/60";
  return (
    <div className="rounded-lg border border-white/10 bg-white/[0.03] px-3 py-3 text-center">
      <div className="text-[10px] uppercase tracking-wider text-white/40 font-bold">{label}</div>
      <div className={`text-2xl font-black mt-0.5 ${color}`}>{value}</div>
    </div>
  );
}
function prettyLabel(k) {
  return k.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}
function normalizeVideoEmbed(url) {
  if (!url) return "";
  // YouTube watch?v=... -> embed
  const m1 = url.match(/youtube\.com\/watch\?v=([^&]+)/);
  if (m1) return `https://www.youtube.com/embed/${m1[1]}`;
  const m2 = url.match(/youtu\.be\/([^?]+)/);
  if (m2) return `https://www.youtube.com/embed/${m2[1]}`;
  return url;
}
